/**
 * tripHighlights.ts — the places worth stopping for along a shared trip.
 *
 * Wikipedia knows where the gorges, falls, beaches and lighthouses are, and
 * its page views say which ones people care about. For each drive day we look
 * up articles within 10 km of points along the day's road, keep the scenic
 * ones (by their short description: "Gorge in Victoria, Australia"), and rank
 * them by views. That gives:
 *
 *   • 2-3 highlight chips per day (Gorge · Waterfalls · Beach)
 *   • a photo strip per day (Loch Ard Gorge, Erskine Falls …)
 *   • the trip's hero: the most-viewed highlight with a wide photo, so the
 *     hero shows the route, not a car park
 *
 * Photos are FREE-LICENSED only (pilicense=free → Wikimedia Commons), and
 * every one is credited. Pure functions up top; the fetch is at the bottom.
 */

export type LatLng = { lat: number; lng: number };

export interface WikiGeoPage {
  pageid?: number;
  title?: string;
  description?: string;
  thumbnail?: { source?: string; width?: number; height?: number };
  pageviews?: Record<string, number | null>;
  coordinates?: { lat: number; lon: number }[];
}

export interface Highlight {
  title: string;
  /** Short category for a chip: "Gorge", "Waterfall", "Beach". */
  kind: string;
  description: string;
  /** Wide photo (hero size when available). */
  photo: string;
  photoWidth: number;
  photoHeight: number;
  url: string;
  views: number;
  position?: LatLng;
}

/** Description words that make a place worth a detour, and the chip they earn. */
const KINDS: [RegExp, string][] = [
  [/\bgorge\b|\bcanyon\b/i, 'Gorge'],
  [/\bwaterfall|\bfalls\b|cascade/i, 'Waterfall'],
  [/\blighthouse\b/i, 'Lighthouse'],
  [/\brock (?:arch|formation|stack)|\bsea stack|\barch\b|\bblowhole\b/i, 'Rock formation'],
  [/\bbeach\b/i, 'Beach'],
  [/\bcape\b|\bheadland\b|\bpromontory\b/i, 'Headland'],
  [/\blookout\b|\bviewpoint\b|\bsummit\b|\bmountain\b|\bpeak\b/i, 'Lookout'],
  [/\bnational park\b|\bstate park\b|\bconservation park\b|\bstate forest\b|\brainforest\b/i, 'National park'],
  [/\bcave\b|\bcaves\b/i, 'Cave'],
  [/\blake\b|\blagoon\b|\binlet\b|\bbay\b|\bestuary\b/i, 'Bay & lake'],
  [/\bisland\b|\breef\b/i, 'Island'],
  [/\bpier\b|\bjetty\b|\bbridge\b/i, 'Pier'],
  [/\bbotanic|\bgarden\b/i, 'Garden'],
  [/\bvolcano|\bcrater\b|\bmaar\b/i, 'Volcano'],
  [/\bwildlife\b|\bnature reserve\b|\bsanctuary\b|\bflora and fauna reserve\b/i, 'Wildlife'],
];

/** Never a highlight, whatever else the description says. */
const DROP =
  /\b(?:ship|shipwreck|steamship|vessel|school|college|university|electoral|railway|station|suburb|town|city|locality|shire|parish|county|company|church|football|cricket|hotel|motel|hospital|airport|road|highway|street|mine|people|person|politician|band|album|film|river|museum|aviation|military|base|prison|cemetery)\b/i;

/**
 * Lead images that aren't photos of the place: paintings, maps, logos, signs.
 * (Lake Purrumbete's lead image is a von Guérard oil painting.)
 */
const NOT_A_PHOTO =
  /painting|art_project|von_gu|oil_on|canvas|lithograph|engraving|etching|watercolou?r|sketch|drawing|illustration|poster|stamp|\bmap\b|_map|map_|locator|location|logo|emblem|coat_of_arms|\bflag\b|_flag|seal_of|signage|_sign\b|sign\.|diagram|\.svg|\.png/i;

/**
 * Archive scans: a year before 1980 in the file name ("Corio_bay_1927.jpg",
 * "Phantom_Falls_1907.jpg"), or the words archives use. They are usually
 * black and white, and they make a trip look like a history lesson.
 */
const ARCHIVAL = /(?:^|[^0-9])(?:1[6-8]\d\d|19[0-7]\d)(?:[^0-9]|$)|aerial|historic|postcard|black_and_white|\bb&w\b|monochrome|slv_|state_library|_c\.\d/i;

/** A real, current photograph of the place (a JPEG that isn't art, a map, a sign or an archive scan). */
export function isPlacePhoto(url: string): boolean {
  let file = url;
  try {
    file = decodeURIComponent(url.split('?')[0]);
  } catch {
    /* keep the raw url */
  }
  const name = file.split('/').pop() || file;
  return /\.jpe?g($|\/)/i.test(file) && !NOT_A_PHOTO.test(file) && !ARCHIVAL.test(name.replace(/^\d+px-/, ''));
}

export function highlightKind(description: string | undefined, title = ''): string | null {
  const d = (description || '').trim();
  if (!d || DROP.test(d)) return null;
  for (const [re, kind] of KINDS) if (re.test(d)) return kind;
  // Some descriptions are bare ("Australian national park"); the title helps.
  for (const [re, kind] of KINDS) if (re.test(title) && !DROP.test(title)) return kind;
  return null;
}

export function pageViews(p: WikiGeoPage): number {
  return Object.values(p.pageviews || {}).reduce<number>((s, v) => s + (typeof v === 'number' ? v : 0), 0);
}

export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Points every `everyKm` along a line (both ends included), at most `max`.
 * Points within `avoidKm` of any `avoid` point are skipped (the home city:
 * its stadiums and museums aren't the trip).
 */
export function samplePath(
  path: LatLng[],
  { everyKm = 30, max = 6, avoid = [], avoidKm = 20 }: { everyKm?: number; max?: number; avoid?: LatLng[]; avoidKm?: number } = {}
): LatLng[] {
  if (!path.length) return [];
  const raw: LatLng[] = [path[0]];
  let since = 0;
  for (let i = 1; i < path.length; i += 1) {
    since += haversineKm(path[i - 1], path[i]);
    if (since >= everyKm) {
      raw.push(path[i]);
      since = 0;
    }
  }
  const last = path[path.length - 1];
  if (raw[raw.length - 1] !== last) raw.push(last);
  const kept = raw.filter((p) => !avoid.some((a) => haversineKm(a, p) < avoidKm));
  if (kept.length <= max) return kept;
  // Spread the budget evenly along the day.
  const step = (kept.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => kept[Math.round(i * step)]);
}

const WIDE = 1.2;

/** Wikipedia pages → ranked, de-duplicated highlights (most viewed first). */
export function rankHighlights(pages: WikiGeoPage[], { min = 0 }: { min?: number } = {}): Highlight[] {
  const seen = new Set<string>();
  const out: Highlight[] = [];
  for (const p of pages) {
    const title = (p.title || '').trim();
    if (!title || seen.has(title)) continue;
    const kind = highlightKind(p.description, title);
    const src = p.thumbnail?.source;
    if (!kind || !src || !/^https:\/\//.test(src) || !isPlacePhoto(src)) continue;
    seen.add(title);
    const c = p.coordinates?.[0];
    out.push({
      title,
      kind,
      description: p.description || '',
      photo: src,
      photoWidth: p.thumbnail?.width ?? 0,
      photoHeight: p.thumbnail?.height ?? 0,
      url: `https://en.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, '_'))}`,
      views: pageViews(p),
      position: c ? { lat: c.lat, lng: c.lon } : undefined,
    });
  }
  return out.filter((h) => h.views >= min).sort((a, b) => b.views - a.views);
}

/** Kinds that make a cover, best first: open views beat close-ups. */
const HERO_WEIGHT: Record<string, number> = {
  Beach: 1,
  'Rock formation': 1,
  Headland: 0.95,
  Gorge: 0.95,
  Waterfall: 0.85,
  Lighthouse: 0.8,
  'National park': 0.75,
  'Bay & lake': 0.7,
  Island: 0.7,
  Lookout: 0.6,
  Volcano: 0.6,
  Cave: 0.5,
  Pier: 0.5,
  Wildlife: 0.45,
  Garden: 0.4,
};

const COAST_KINDS = new Set(['Beach', 'Rock formation', 'Headland', 'Lighthouse', 'Bay & lake', 'Island', 'Pier']);
const HILL_KINDS = new Set(['Lookout', 'Waterfall', 'National park', 'Gorge', 'Volcano', 'Cave']);

/** What the trip's name promises: "Great Ocean Road" → coast; "High Country" → hills. */
export function tripTheme(title = ''): 'coast' | 'hills' | null {
  if (/\b(ocean|coast(al)?|beach(es)?|surf|bays?|seas?|seaside|islands?|reef|capes?|shore|harbour|peninsula)\b/i.test(title)) return 'coast';
  if (/\b(mountains?|alpine|alps|ranges?|high country|falls|waterfalls?|rainforest|gorges?|outback|hinterland|forest)\b/i.test(title)) return 'hills';
  return null;
}

/**
 * How good a cover this highlight makes: the kind of view, how well known it
 * is, and whether it's what the trip's name promises.
 */
export function heroScore(h: Highlight, theme: 'coast' | 'hills' | null = null): number {
  const weight = HERO_WEIGHT[h.kind] ?? 0.35;
  const onTheme = theme === 'coast' ? COAST_KINDS.has(h.kind) : theme === 'hills' ? HILL_KINDS.has(h.kind) : false;
  const offTheme = theme !== null && !onTheme;
  const ratio = h.photoHeight > 0 ? h.photoWidth / h.photoHeight : 0;
  return weight * (onTheme ? 1.6 : offTheme ? 0.7 : 1) * (ratio >= 1.4 ? 1.1 : 1) * (1 + Math.log10(Math.max(0, h.views) + 10));
}

/** How close counts as "at the first night's stop" for the cover. */
export const NEAR_NIGHT_KM = 12;

/**
 * Highlights that could be the cover (wide, big photos), best first. `near`
 * is the first overnight stop: a view within NEAR_NIGHT_KM of it wins, so the
 * cover is where the trip first sleeps (Lorne Beach, not a misty beach an
 * hour back up the road), and every visitor gets the same picture.
 */
export function heroCandidates(
  highlights: Highlight[],
  { minWidth = 1000, title = '', max = 4, near = null }: { minWidth?: number; title?: string; max?: number; near?: LatLng | null } = {}
): Highlight[] {
  const theme = tripTheme(title);
  const atNight = (h: Highlight) => !!near && !!h.position && haversineKm(near, h.position) <= NEAR_NIGHT_KM;
  return highlights
    .filter((h) => h.photoWidth >= minWidth && h.photoHeight > 0 && h.photoWidth / h.photoHeight >= WIDE && isPlacePhoto(h.photo))
    .map((h, i) => ({ h, i, s: heroScore(h, theme) * (atNight(h) ? 2 : 1) }))
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, max)
    .map((x) => x.h);
}

/** The trip hero: the best cover among the highlights (see heroCandidates). */
export function pickHighlightHero(highlights: Highlight[], opts: number | { minWidth?: number; title?: string } = {}): Highlight | null {
  const o = typeof opts === 'number' ? { minWidth: opts } : opts;
  return heroCandidates(highlights, { ...o, max: 1 })[0] ?? null;
}

/**
 * Why to stop, in one line, from the article's opening paragraph: the
 * sentence that says what you do there (walk, swim, look, see wildlife), not
 * the one about spelling, councils or who it's named after. Asides in commas
 * ("sometimes incorrectly spelled as …,") are cut. Null when nothing reads
 * as a reason; then the card shows the place's kind only.
 */
const HOOK_WORDS =
  /\b(?:walk(?:s|ing)?|track|trail|loop|foot|causeway|swim(?:ming)?|surf(?:ing)?|calm|snorkel|views?|lookout|panoram|sunset|sunrise|drop|metres?|tall(?:est)?|highest|plunge|cascade|colony|wildlife|birds?|penguins?|seals?|koalas?|wallab(?:y|ies)|kangaroos?|whales?|dolphins?|picnic|lighthouse|cliffs?|limestone|arch|rock pools?|fern|rainforest|waterfall|popular|famous|iconic|sheltered|patrolled)\b/gi;
const HOOK_NOISE =
  /\b(?:spelled|spelt|named (?:after|for)|managed by|council|shire|parish|electoral|census|population|history|historic|settlement|gazetted|heritage[- ]listed|inhabitants|located (?:in|at|on)|is (?:a|an|the) [a-z ]*(?:in|of) (?:the )?(?:state|region))\b/i;

export function stopHook(extract: string | null | undefined, maxChars = 120): string | null {
  const text = (extract || '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  // Asides: ", sometimes incorrectly spelled as Griffith Island or Griffitts Island,"
  const clean = text
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/,\s*(?:sometimes|also|formerly|officially|commonly|often)\b[^,]*,/gi, '');
  const sentences = clean.match(/[^.!?]+[.!?]+/g) || [clean];
  let best: { s: string; score: number } | null = null;
  sentences.forEach((raw, i) => {
    let s = raw.trim();
    // "X now has no permanent inhabitants, but is connected …" → "Connected …"
    const but = s.match(/,\s*but\s+(?:is\s+|it\s+is\s+)?(.+)$/i);
    if (but) s = but[1];
    s = s.replace(/^(?:it|this|the (?:beach|island|falls|waterfall|gorge|park|lighthouse|lookout))\s+(?:is|has)\s+(?:also\s+)?/i, '');
    s = s.replace(/\band is (accessible|home|known)/i, 'and $1');
    const hits = (s.match(HOOK_WORDS) || []).length;
    if (!hits || HOOK_NOISE.test(s)) return;
    const score = hits * 2 - i * 0.5 - (s.length > maxChars ? 1 : 0);
    if (!best || score > best.score) best = { s, score };
  });
  if (!best) return null;
  let out = (best as { s: string }).s.trim();
  out = out.charAt(0).toUpperCase() + out.slice(1);
  if (!/[.!?]$/.test(out)) out += '.';
  if (out.length > maxChars) {
    const cut = out.slice(0, maxChars - 1);
    out = `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 40)).replace(/[,;:\s]+$/, '')}…`;
  }
  return out;
}

/** Up to `n` distinct chip labels for a day, in rank order. */
export function highlightChips(highlights: Highlight[], n = 3): string[] {
  const out: string[] = [];
  for (const h of highlights) {
    if (!out.includes(h.kind)) out.push(h.kind);
    if (out.length >= n) break;
  }
  return out;
}

/**
 * Split highlights across days: each place shows on the first day that
 * passes it, so the strip never repeats itself.
 */
export function assignToDays(perDay: Highlight[][], perDayMax = 4): Highlight[][] {
  const used = new Set<string>();
  return perDay.map((list) => {
    const mine: Highlight[] = [];
    for (const h of list) {
      if (used.has(h.title)) continue;
      used.add(h.title);
      mine.push(h);
      if (mine.length >= perDayMax) break;
    }
    return mine;
  });
}

/** A Wikimedia thumbnail at another width (`/1600px-` → `/480px-`). */
export function resizeWikiThumb(url: string, width: number): string {
  return /\/\d+px-/.test(url) ? url.replace(/\/\d+px-/, `/${width}px-`) : url;
}

/* ── Fetch ──────────────────────────────────────────────────────────────── */

const GEO_PROPS = 'prop=pageimages|description|pageviews|coordinates&pvipdays=30&piprop=thumbnail&pithumbsize=1600&pilicense=free';

async function geoPages(point: LatLng, timeoutMs: number): Promise<WikiGeoPage[]> {
  const url =
    'https://en.wikipedia.org/w/api.php?action=query&format=json&origin=*' +
    `&generator=geosearch&ggscoord=${point.lat.toFixed(4)}|${point.lng.toFixed(4)}&ggsradius=10000&ggslimit=30&${GEO_PROPS}`;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    // On the server this caches for a day; browsers ignore `next`.
    const res = await fetch(url, {
      signal: ctrl?.signal,
      headers: { Accept: 'application/json' },
      next: { revalidate: 86400 },
    } as RequestInit);
    if (!res.ok) return [];
    const data = await res.json();
    return data?.query?.pages ? (Object.values(data.query.pages) as WikiGeoPage[]) : [];
  } catch {
    return [];
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Highlights near each list of points (one list per drive day), ranked. */
export async function fetchHighlights(
  pointsPerDay: LatLng[][],
  { timeoutMs = 4000 }: { timeoutMs?: number } = {}
): Promise<Highlight[][]> {
  const results = await Promise.all(
    pointsPerDay.map(async (points) => {
      const pages = (await Promise.all(points.map((p) => geoPages(p, timeoutMs)))).flat();
      return rankHighlights(pages);
    })
  );
  return results;
}
